/**
 * PortfolioTab Component
 *
 * Top-level "my Impact Portfolio" surface for employees.
 *
 * Design intent (from HR/people-leader feedback):
 *  - A safe, judgement-free space to capture evidence as you go.
 *  - The portfolio is *private to you* until you explicitly share it for a
 *    specific check-in (mid-year / year-end). Sharing happens in the
 *    check-in preparation phase, not entry-by-entry.
 *  - "Simple" view (default) hides classification, validation, At-Risk pills,
 *    progress %s, and other metric-anxiety UI. "Detailed" view restores them
 *    for managers / HR / power users.
 *  - This tab wraps the existing `EvidencePortfolio` component (which already
 *    handles add/edit/delete/upload) with a softer, journal-style header and
 *    the shared `useJourneyViewMode` toggle.
 */

import { Lock } from 'lucide-react'
import EvidencePortfolio from '@/components/performance/EvidencePortfolio'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import type { EnhancedEvidenceEntry, KPAData } from '@/types/evidence'

/**
 * Glossary surfaced as `?` tooltips next to key terms — first request from HR
 * was "add helpful ? bubbles to define certain terms".
 */
export const PORTFOLIO_GLOSSARY = {
  impactPortfolio: 'Your impact portfolio — wins, lessons, and growth moments — that fuels your check-in conversations.',
  kpa: 'Key Performance Area: a high-level area of your work, like Operating & Financial Results or Culture & People.',
  midYear: 'A reflection point around June where you and your people leader review your progress.',
  yearEnd: 'A reflection point at year-end celebrating impact and shaping the next year\'s growth.',
  evidence: 'A short note about something you\'ve done — a project, a conversation, a contribution. You decide when (and whether) to share it.',
  privateToYou: 'Only you can see your portfolio entries. You decide what to surface at your next check-in.',
} as const

interface PortfolioTabProps {
  user: { id?: string; employeeId?: string; displayName?: string } | null
  evidence: EnhancedEvidenceEntry[]
  kpaData: KPAData[]
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined>
  onUpdateEvidence: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onDeleteEvidence: (evidenceId: string) => Promise<void>
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => Promise<void>
  /** "Continue at Year-End" handler — see performanceService.carryForwardEvidence. */
  onCarryForwardEvidence?: (evidenceId: string) => Promise<{
    evidence: EnhancedEvidenceEntry
    kpi_carry_forward_warning: 'kpi_no_longer_active' | null
    idempotent: boolean
  } | undefined>
  /** Binary release: share / make-private a single entry. */
  onReleaseEvidence?: (evidenceId: string) => Promise<EnhancedEvidenceEntry | undefined>
  onWithdrawEvidence?: (evidenceId: string) => Promise<EnhancedEvidenceEntry | undefined>
  onRefreshEvidence: () => Promise<void>
}

export function PortfolioTab({
  user,
  evidence,
  kpaData,
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onUpdateEvidenceClassification,
  onCarryForwardEvidence,
  onReleaseEvidence,
  onWithdrawEvidence,
  onRefreshEvidence,
}: PortfolioTabProps) {
  // The Impact Portfolio is always the calm Simple view — a judgement-free
  // journal, not a metrics surface. No Show details toggle here (the global
  // journey toggle still governs the Objectives surface).
  const viewMode = 'simple' as const
  const userId = user?.employeeId

  // Build the available KPIs list across ALL KPAs so the linked-KPI selector in
  // EvidencePortfolio works for every entry, not just one KPA's worth.
  // Each KPI carries its parent KPA id so the form can narrow the KPI Select
  // to just that KPA's KPIs once the user picks a KPA in the Add Entry dialog.
  const availableKPIs = kpaData.flatMap((kpa) =>
    (kpa.underlyingKPIs ?? []).map((kpi: any) => ({
      id: kpi.id,
      name: kpi.name,
      kpaId: kpa.numericId || kpa.id,
      objectiveTitle: kpa.individualObjectives.find((obj) => obj.id === kpi.objectiveId)?.title,
      current_value: kpi.current_value ?? undefined,
      target_value: kpi.target_value,
      unit: kpi.unit,
      measurement_history: (kpi as any)?.measurement_history,
    }))
  )

  // KPA picker options for the Add Entry dialog. The Portfolio surface
  // isn't pre-bound to any KPA (kpaId={undefined}) — by passing the list of
  // KPAs we let the user explicitly link a new entry to one, or leave it
  // as a **General** entry (kpaId=null). HR feedback explicitly asked for
  // a General bucket for impact items that don't tie to a specific KPA.
  const kpaOptions = kpaData.map((kpa) => ({
    id: kpa.numericId || kpa.id,
    name: kpa.display_name || kpa.name,
  }))

  return (
    <div className="space-y-6">
      {/* Header — editorial chapter opener. The Impact Portfolio is always the
          calm Simple view (no Show details toggle): a judgement-free journal,
          not a metrics dashboard. The top padding gives it room to breathe
          below the journey tab strip. */}
      <div className="border-b border-stone-200 pt-8 md:pt-12 pb-8 md:pb-10">
        <div className="max-w-[720px] mx-auto">
          <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-3 inline-flex items-center gap-2">
            <Lock className="h-3 w-3" />
            Private to you
            <InfoTooltip text={PORTFOLIO_GLOSSARY.privateToYou} />
          </p>
          {/* Page title intentionally omitted — the "my Impact Portfolio" nav
              tab already names this surface. The privacy chip + standfirst
              below carry the framing. */}
          <p className="font-editorial italic text-quarterly-ink-muted text-[18px] md:text-[20px] leading-[1.55] max-w-[600px]">
            Capture your impact portfolio as it happens — wins, lessons, growth moments. You decide what to share with your people leader.
          </p>
        </div>
      </div>

      {/* Reuse the existing EvidencePortfolio component with kpaId={undefined}
          so it shows ALL evidence across all KPAs — there's no per-KPA filter
          on this top-level surface. Constrained to the same 720px reading
          column the header uses, so the entries read as a continuous
          editorial column. */}
      <div className="max-w-[720px] mx-auto">
        <EvidencePortfolio
          evidence={evidence}
          userRole="employee"
          currentUserId={userId}
          targetUserId={userId}
          kpaId={undefined}
          kpaOptions={kpaOptions}
          availableKPIs={availableKPIs}
          viewMode={viewMode}
          readOnly={false}
          hideUploadButton={false}
          onAddEvidence={onAddEvidence}
          onUpdateEvidence={onUpdateEvidence}
          onDeleteEvidence={onDeleteEvidence}
          onUploadDocument={onUploadDocument}
          onClassifyEvidence={onUpdateEvidenceClassification}
          onCarryForwardEvidence={onCarryForwardEvidence}
          onReleaseEvidence={onReleaseEvidence}
          onWithdrawEvidence={onWithdrawEvidence}
          onRefreshEvidence={onRefreshEvidence}
        />
      </div>
    </div>
  )
}

export default PortfolioTab
